import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { useResizeObserver } from "@web/hooks/useResizeObserver"
import { LexicalEditor } from "lexical"
import React, { PropsWithChildren, RefObject, useCallback } from "react"

/**
 * Context for outer components to control the TextEditor
 */
export type TextEditorControls = {
  insertVariable?: (value: string) => void
  setTextContent?: (value: string) => void
}

export type TextEditorControlsSetter = React.Dispatch<React.SetStateAction<TextEditorControls>>

interface TextEditorController {
  setControls: (controls: Partial<TextEditorControls>) => void
}

export const TextEditorControllerContext = React.createContext<TextEditorController>({
  setControls: () => {},
})

export const TextEditorControllerContextProvider = ({
  children,
  setControls,
}: PropsWithChildren & {
  setControls?: TextEditorControlsSetter
}) => {
  const _setControls = useCallback(
    (controls: Partial<TextEditorControls>) => {
      setControls?.((c) => ({ ...c, ...controls }))
    },
    [setControls]
  )

  if (!setControls) {
    return <>{children}</>
  }

  return (
    <TextEditorControllerContext.Provider value={{ setControls: _setControls }}>
      {children}
    </TextEditorControllerContext.Provider>
  )
}

export const useTextEditorControllerContext = () => {
  return React.useContext(TextEditorControllerContext)
}

/**
 * Inner context of the TextEditor
 */
export type TextEditorContext = {
  triggerOnChange: () => void
  inputWidth?: number
  disabledAssetTypes?: string[]
}

const TextEditorContext = React.createContext<TextEditorContext>({ triggerOnChange: () => {} })

export const useTextEditorContext = () => {
  return React.useContext(TextEditorContext)
}

export const TextEditorContextProvider = ({
  children,
  disabledAssetTypes,
  triggerOnChange,
  editorRootRef,
}: PropsWithChildren & {
  disabledAssetTypes?: string[]
  editorRootRef: RefObject<HTMLDivElement>
  triggerOnChange: (_: unknown, editor: LexicalEditor) => void
}) => {
  const [editor] = useLexicalComposerContext()
  const inputRef = React.useRef<HTMLDivElement | null>(null)

  const handleTriggerOnChange = React.useCallback(() => {
    triggerOnChange(null, editor)
  }, [editor, triggerOnChange])

  React.useEffect(
    function getInputRef() {
      if (!editorRootRef.current) return

      inputRef.current = editorRootRef.current.querySelector(`#editor-input`) as HTMLDivElement

      // Make sure the input is scrolled to the top initially
      requestAnimationFrame(() => inputRef.current?.scrollTo?.(0, 0))
    },
    [editorRootRef]
  )

  const { width: inputWidth } = useResizeObserver(inputRef)

  return (
    <TextEditorContext.Provider value={{ triggerOnChange: handleTriggerOnChange, inputWidth, disabledAssetTypes }}>
      {children}
    </TextEditorContext.Provider>
  )
}
